import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Collapse,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Divider,
  alpha,
} from '@mui/material';
import {
  EventNote,
  AttachMoney,
  Person,
  LocalHospital,
  MedicalInformation,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createRecord, updateRecord, getRecord, CreateRecordData, UpdateRecordData, MedicalRecord } from '../api/api';

const RecordForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const shouldAddPayment = searchParams.get('addPayment') === 'true';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [includePayment, setIncludePayment] = useState(false);
  const [paymentSectionOpen, setPaymentSectionOpen] = useState(false);

  const [formData, setFormData] = useState({
    record_date: new Date().toISOString().split('T')[0],
    record_time: '',
    patient_name: '',
    doctor_name: '',
    hospital_name: '',
    procedure_name: '',
    procedure_description: '',
    insurance_name: '',
    amount_paid: '',
    record_type: 'appointment' as 'appointment' | 'payment',
    comments: '',
    duration: '',
    material_cost: '',
    travel_expenses: '',
  });

  useEffect(() => {
    if (isEdit && id) {
      loadRecord(parseInt(id));
    } else if (shouldAddPayment) {
      // If coming from "Add Payment" button but no ID, something's wrong
      setError('Record ID is required to add payment');
    }
  }, [isEdit, id, shouldAddPayment]);

  const loadRecord = async (recordId: number) => {
    setLoading(true);
    try {
      const record: MedicalRecord = await getRecord(recordId);
      const hasPayment = record.amount_paid && record.amount_paid > 0;
      
      setFormData({
        record_date: record.record_date,
        record_time: record.record_time || '',
        patient_name: record.patient_name || '',
        doctor_name: record.doctor_name || '',
        hospital_name: record.hospital_name || '',
        procedure_name: record.procedure_name || '',
        procedure_description: record.procedure_description || '',
        insurance_name: record.insurance_name || '',
        amount_paid: record.amount_paid?.toString() || '',
        record_type: record.record_type,
        comments: record.comments || '',
        duration: record.duration?.toString() || '',
        material_cost: record.material_cost?.toString() || '',
        travel_expenses: record.travel_expenses?.toString() || '',
      });
      
      // Set payment section state based on existing data or URL parameter
      const showPaymentSection = hasPayment || shouldAddPayment;
      setIncludePayment(showPaymentSection);
      setPaymentSectionOpen(showPaymentSection);
    } catch (error) {
      setError('Failed to load record');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      const recordData: CreateRecordData | UpdateRecordData = {
        record_date: formData.record_date,
        record_time: formData.record_time || undefined,
        patient_name: formData.patient_name || undefined,
        doctor_name: formData.doctor_name || undefined,
        hospital_name: formData.hospital_name || undefined,
        procedure_name: formData.procedure_name || undefined,
        procedure_description: formData.procedure_description || undefined,
        insurance_name: formData.insurance_name || undefined,
        amount_paid: formData.amount_paid ? parseFloat(formData.amount_paid) : undefined,
        record_type: formData.record_type,
        comments: formData.comments || undefined,
        duration: formData.duration ? parseInt(formData.duration) : undefined,
        material_cost: formData.material_cost ? parseFloat(formData.material_cost) : undefined,
        travel_expenses: formData.travel_expenses ? parseFloat(formData.travel_expenses) : undefined,
      };

      if (isEdit && id) {
        const result = await updateRecord(parseInt(id), recordData);
        setSuccess(result.message);
      } else {
        const result = await createRecord(recordData as CreateRecordData);
        setSuccess(result.message);
        // Reset form for new record
        if (!isEdit) {
          setFormData({
            record_date: new Date().toISOString().split('T')[0],
            record_time: '',
            patient_name: '',
            doctor_name: '',
            hospital_name: '',
            procedure_name: '',
            procedure_description: '',
            insurance_name: '',
            amount_paid: '',
            record_type: 'appointment',
            comments: '',
            duration: '',
            material_cost: '',
            travel_expenses: '',
          });
        }
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numbers, dots, and commas
    const cleaned = value.replace(/[^\d.,]/g, '');
    setFormData(prev => ({ ...prev, amount_paid: cleaned }));
  };

  const handleMaterialCostChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numbers, dots, and commas
    const cleaned = value.replace(/[^\d.,]/g, '');
    setFormData(prev => ({ ...prev, material_cost: cleaned }));
  };

  const handleTravelExpensesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numbers, dots, and commas
    const cleaned = value.replace(/[^\d.,]/g, '');
    setFormData(prev => ({ ...prev, travel_expenses: cleaned }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Medical Record' : 'Add New Medical Record'}
      </Typography>

      <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Medical Record Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <MedicalInformation sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Medical Record Information
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.record_date}
                onChange={handleChange('record_date')}
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={formData.record_time}
                onChange={handleChange('record_time')}
                InputLabelProps={{ shrink: true }}
                helperText="Optional - appointment time"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Patient Name"
                value={formData.patient_name}
                onChange={handleChange('patient_name')}
                helperText="Patient's full name"
                InputProps={{
                  startAdornment: <Person sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
              <TextField
                fullWidth
                label="Doctor Name"
                value={formData.doctor_name}
                onChange={handleChange('doctor_name')}
                helperText="Doctor's full name"
                InputProps={{
                  startAdornment: <LocalHospital sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
            </Stack>

            <TextField
              fullWidth
              label="Hospital"
              value={formData.hospital_name}
              onChange={handleChange('hospital_name')}
              helperText="Hospital or clinic name"
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Procedure Name"
                value={formData.procedure_name}
                onChange={handleChange('procedure_name')}
                helperText="Standard procedure name"
              />
              <TextField
                fullWidth
                label="Insurance Company"
                value={formData.insurance_name}
                onChange={handleChange('insurance_name')}
                helperText="Payment source or insurance"
              />
            </Stack>

            <TextField
              fullWidth
              label="Procedure Description"
              multiline
              rows={3}
              value={formData.procedure_description}
              onChange={handleChange('procedure_description')}
              helperText="Detailed description of the procedure"
            />

            {/* Additional Record Details */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <MedicalInformation sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Additional Details
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={formData.duration}
                onChange={handleChange('duration')}
                helperText="Procedure duration in minutes"
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                label="Material Cost (R$)"
                value={formData.material_cost}
                onChange={handleMaterialCostChange}
                helperText="Cost of materials used"
                placeholder="0,00"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Travel Expenses (R$)"
                value={formData.travel_expenses}
                onChange={handleTravelExpensesChange}
                helperText="Transportation/travel costs"
                placeholder="0,00"
              />
              <Box sx={{ flex: 1 }} /> {/* Spacer for layout */}
            </Stack>

            <TextField
              fullWidth
              label="Comments"
              multiline
              rows={2}
              value={formData.comments}
              onChange={handleChange('comments')}
              helperText="Additional notes or comments"
            />

            <Divider sx={{ my: 3 }} />

            {/* Payment Section Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AttachMoney sx={{ color: 'secondary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Payment Information
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={includePayment}
                    onChange={(e) => {
                      setIncludePayment(e.target.checked);
                      setPaymentSectionOpen(e.target.checked);
                      if (!e.target.checked) {
                        // Clear payment fields when disabled
                        setFormData(prev => ({
                          ...prev,
                          amount_paid: '',
                          record_type: 'appointment'
                        }));
                      }
                    }}
                    color="secondary"
                  />
                }
                label="Include payment details"
                labelPlacement="start"
              />
            </Box>

            {/* Collapsible Payment Section */}
            <Collapse in={paymentSectionOpen} timeout="auto" unmountOnExit>
              <Card 
                sx={{ 
                  p: 3, 
                  backgroundColor: alpha('#f50057', 0.05),
                  border: `1px solid ${alpha('#f50057', 0.2)}`,
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add payment information for this medical record. This is optional and can be added later.
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <FormControl fullWidth>
                        <InputLabel>Record Type</InputLabel>
                        <Select
                          value={includePayment ? 'payment' : formData.record_type}
                          label="Record Type"
                          onChange={handleChange('record_type')}
                          required
                        >
                          <MenuItem value="appointment">Appointment Only</MenuItem>
                          <MenuItem value="payment">Appointment with Payment</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Amount Paid (R$)"
                        value={formData.amount_paid}
                        onChange={handleAmountChange}
                        helperText="Enter amount in Brazilian Real"
                        placeholder="0,00"
                        InputProps={{
                          startAdornment: <AttachMoney sx={{ color: 'action.active', mr: 1 }} />
                        }}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Collapse>

            <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/records')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <EventNote />}
                sx={{ minWidth: 160 }}
              >
                {saving ? 'Saving...' : isEdit ? 'Update Record' : 'Create Record'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default RecordForm;